import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  IAcceptTrainerInvitationRequest,
  IInviteTrainerRequest,
  IRejectTrainerInvitationRequest,
  IRemoveUserFromOrganizationRequest,
  IRevokeTrainerRoleRequest
} from '@treneo/models';
import { ResultHelpers } from '@treneo/models';

import { organizationsApi } from '../api';
import { TRAINER_INVITATION_QUERY_KEY } from '../organizations-query-keys';
import {
  MANAGED_ORGANIZATION_USERS_QUERY_KEY,
  MY_MANAGED_ORGANIZATIONS_QUERY_KEY,
  MY_PENDING_TRAINER_INVITATIONS_QUERY_KEY,
  ORGANIZATION_INVITATIONS_QUERY_KEY,
  ORGANIZATION_MEMBERSHIP_HISTORY_QUERY_KEY,
  ORGANIZATION_TRAINERS_QUERY_KEY,
  ORGANIZATION_USERS_QUERY_KEY,
  TRAINER_INVITATION_BY_TOKEN_QUERY_KEY
} from '../queries/organizations-queries';

export const useInviteTrainerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: IInviteTrainerRequest) =>
      ResultHelpers.unwrap(
        await organizationsApi.inviteTrainer(request),
        'organizations.errors.inviteTrainerFailed'
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_TRAINERS_QUERY_KEY, variables.organizationId]
      });
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_INVITATIONS_QUERY_KEY, variables.organizationId]
      });
    }
  });
};

const invalidateInvitationQueries = (
  queryClient: ReturnType<typeof import('@tanstack/react-query').useQueryClient>,
  organizationId: string
): void => {
  queryClient.invalidateQueries({
    queryKey: [ORGANIZATION_TRAINERS_QUERY_KEY, organizationId]
  });
  queryClient.invalidateQueries({
    queryKey: [ORGANIZATION_INVITATIONS_QUERY_KEY, organizationId]
  });
  queryClient.invalidateQueries({
    queryKey: [ORGANIZATION_USERS_QUERY_KEY, organizationId]
  });
  queryClient.invalidateQueries({ queryKey: [MY_MANAGED_ORGANIZATIONS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [MANAGED_ORGANIZATION_USERS_QUERY_KEY] });
  queryClient.invalidateQueries({
    queryKey: [TRAINER_INVITATION_QUERY_KEY, organizationId]
  });
  queryClient.invalidateQueries({ queryKey: [MY_PENDING_TRAINER_INVITATIONS_QUERY_KEY] });
};

export const useAcceptTrainerInvitationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: IAcceptTrainerInvitationRequest) =>
      ResultHelpers.unwrap(
        await organizationsApi.acceptTrainerInvitation(request),
        'organizations.errors.acceptTrainerInvitationFailed'
      ),
    onSuccess: (_, variables) => {
      invalidateInvitationQueries(queryClient, variables.organizationId);
    }
  });
};

export const useAcceptTrainerInvitationPublicByTokenMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { token: string }) =>
      ResultHelpers.unwrap(
        await organizationsApi.acceptTrainerInvitationPublicByToken(request),
        'organizations.errors.acceptTrainerInvitationFailed'
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINER_INVITATION_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [TRAINER_INVITATION_BY_TOKEN_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_PENDING_TRAINER_INVITATIONS_QUERY_KEY] });
    }
  });
};

export const useRejectTrainerInvitationByTokenMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { token: string }) =>
      ResultHelpers.unwrap(
        await organizationsApi.rejectTrainerInvitationByToken(request),
        'organizations.errors.rejectTrainerInvitationFailed'
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINER_INVITATION_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [TRAINER_INVITATION_BY_TOKEN_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_PENDING_TRAINER_INVITATIONS_QUERY_KEY] });
    }
  });
};

export const useRejectTrainerInvitationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: IRejectTrainerInvitationRequest) =>
      ResultHelpers.unwrap(
        await organizationsApi.rejectTrainerInvitation(request),
        'organizations.errors.rejectTrainerInvitationFailed'
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_INVITATIONS_QUERY_KEY, variables.organizationId]
      });
      queryClient.invalidateQueries({
        queryKey: [TRAINER_INVITATION_QUERY_KEY, variables.organizationId]
      });
      queryClient.invalidateQueries({ queryKey: [MY_PENDING_TRAINER_INVITATIONS_QUERY_KEY] });
    }
  });
};

export const useRemoveUserFromOrganizationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: IRemoveUserFromOrganizationRequest) =>
      ResultHelpers.unwrap(
        await organizationsApi.removeUserFromOrganization(request),
        'organizations.errors.removeUserFromOrganizationFailed'
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_USERS_QUERY_KEY, variables.organizationId]
      });
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_MEMBERSHIP_HISTORY_QUERY_KEY, variables.organizationId]
      });
      queryClient.invalidateQueries({ queryKey: [MY_MANAGED_ORGANIZATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MANAGED_ORGANIZATION_USERS_QUERY_KEY] });
    }
  });
};

export const useRevokeTrainerRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: IRevokeTrainerRoleRequest) =>
      ResultHelpers.unwrap(
        await organizationsApi.revokeTrainerRole(request),
        'organizations.errors.revokeTrainerRoleFailed'
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_USERS_QUERY_KEY, variables.organizationId]
      });
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_MEMBERSHIP_HISTORY_QUERY_KEY, variables.organizationId]
      });
      queryClient.invalidateQueries({ queryKey: [MY_MANAGED_ORGANIZATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MANAGED_ORGANIZATION_USERS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_TRAINERS_QUERY_KEY, variables.organizationId]
      });
    }
  });
};

export const useCancelOrganizationInvitationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      invitationId
    }: {
      organizationId: string;
      invitationId: string;
    }) =>
      ResultHelpers.unwrap(
        await organizationsApi.cancelOrganizationInvitation(organizationId, invitationId),
        'organizations.errors.cancelOrganizationInvitationFailed'
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_INVITATIONS_QUERY_KEY, variables.organizationId]
      });
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_USERS_QUERY_KEY, variables.organizationId]
      });
    }
  });
};
